// Emails (sidebar; route #/marketing) — formerly the "PSAC board".
// Report (default): SuperSend sends per dealmaker per sequence step, by week —
// the weekly numbers. Board: KPI strip → three queues → the six-column PSAC
// flow, the PSAC rebuilt on real state (plan doc v2.2). Board/Table/Cleaning
// only fetch while open: the board's scoped reads are the slow ones.
// Statuses auto-advance from provider events (ingest controller); the only
// human writes here are the ones events can't see: call held, no-show, and
// close-outs — which always require a reason.

const MKT_FLOW = ['new_prospect', 'email_sent', 'follow_up_sent', 'responded', 'call_scheduled', 'ds_call_held'];

const MKT_LABELS = {
  new_prospect: 'New Prospect', email_sent: 'Email Sent', follow_up_sent: 'Follow-up Sent',
  responded: 'Responded', call_scheduled: 'Call Scheduled', ds_call_held: 'DS Call Held',
};

const MKT_EXIT_LABELS = {
  not_interested: 'Not interested',
  too_small:      'Too small',
  no_show:        'No-show',
  future:         'Future (re-approach)',
  bad_contact:    'Bad contact info',
  unsubscribed:   'Unsubscribed',
};

// Aging chip: quiet → amber past half the SLA → rose past it.
const ageColor = (days, escalation = 30) => {
  if (days == null) return 'var(--text-3)';
  if (days >= escalation) return 'var(--rose-fg, #b42318)';
  if (days >= escalation / 2) return 'var(--amber-fg, #93540a)';
  return 'var(--text-3)';
};

const AgeChip = ({ days, escalation }) => (
  days == null ? null : (
    <span style={{
      fontSize: 10, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
      color: ageColor(days, escalation), border: '1px solid var(--border)',
      borderRadius: 4, padding: '0 4px',
    }}>{days}d</span>
  )
);

// PSAC membership under the DealCloud report's definition: 'in' = has an
// interest tag and no disposition tag; 'out' = interest vetoed by a
// disposition tag (Not Interested / Too Small / Remove from PSAC / …).
const PsacBadge = ({ psac }) => (
  !psac ? null : (
    <span title={psac === 'in'
        ? 'In PSAC — matches the DealCloud PSAC report filters'
        : 'Excluded from PSAC — a disposition tag vetoes the interest tags'}
      style={{ fontSize: 9, fontWeight: 700, whiteSpace: 'nowrap', borderRadius: 4,
               padding: '0 4px', border: '1px solid var(--border)',
               color: psac === 'in' ? 'var(--text-2)' : 'var(--text-3)',
               textDecoration: psac === 'out' ? 'line-through' : 'none' }}>
      PSAC
    </span>
  )
);

// One contact row/card — shared by the queues and the board columns.
const MktCard = ({ card, escalation, onOpen, onOpenCompany, actions }) => (
  <div style={{
    border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface)',
    padding: '6px 8px', display: 'flex', flexDirection: 'column', gap: 2,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <button
        onClick={onOpen}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                 color: 'var(--text-1)', fontSize: 12, fontWeight: 600, textAlign: 'left',
                 overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}
        title={card.email || ''}
      >{card.name || card.email || 'Unknown'}</button>
      <PsacBadge psac={card.psac} />
      <AgeChip days={card.days_in_status} escalation={escalation} />
    </div>
    <div style={{ fontSize: 11, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {card.company_id ? (
        <button onClick={(e) => { e.stopPropagation(); onOpenCompany && onOpenCompany(card); }}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                   color: 'var(--text-2)', fontSize: 11, textDecoration: 'underline',
                   textDecorationColor: 'var(--border)', textUnderlineOffset: 2 }}>
          {card.company}
        </button>
      ) : (card.company || '—')}{card.industry ? ` · ${card.industry}` : ''}
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontSize: 10, color: 'var(--text-3)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {card.owner || 'unassigned'}{card.phone ? ' · ☎' : ''}
      </span>
      {actions}
    </div>
  </div>
);

// Close-out control: a tiny inline reason picker. Exits always need a reason.
const CloseOut = ({ card, onDone }) => {
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const pick = async (exit) => {
    if (busy) return;
    setBusy(true);
    try {
      await window.AtlasApi.setMarketingStatus(card.contact_id, { status: exit, reason: MKT_EXIT_LABELS[exit] });
      onDone();
    } catch (e) {
      alert(`Could not close out: ${e?.message || e}`);
    } finally { setBusy(false); setOpen(false); }
  };
  if (!open) {
    return (
      <button onClick={() => setOpen(true)} title="Close out (requires a reason)"
        style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 4,
                 color: 'var(--text-3)', fontSize: 10, cursor: 'pointer', padding: '0 5px' }}>✕</button>
    );
  }
  return (
    <window.Picker value="" autoOpen onClose={() => setOpen(false)} disabled={busy}
      onChange={(v) => v && pick(v)} size="sm" placeholder="why?" style={{ maxWidth: 130 }} menuMinWidth={180}
      options={Object.entries(MKT_EXIT_LABELS).map(([k, v]) => ({ value: k, label: v }))} />
  );
};

const Advance = ({ card, next, nextLabel, onDone }) => {
  const [busy, setBusy] = React.useState(false);
  if (!next) return null;
  const go = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await window.AtlasApi.setMarketingStatus(card.contact_id, { status: next });
      onDone();
    } catch (e) {
      alert(`Could not move: ${e?.message || e}`);
    } finally { setBusy(false); }
  };
  return (
    <button onClick={go} disabled={busy} title={`Move to ${nextLabel}`}
      style={{ background: 'var(--surface-2, var(--surface))', border: '1px solid var(--border)',
               borderRadius: 4, color: 'var(--text-2)', fontSize: 10, cursor: 'pointer', padding: '0 5px' }}>
      → {nextLabel}
    </button>
  );
};

// Muted count badge — replaces the "(0)" noise. Hidden entirely at zero.
const CountBadge = ({ n }) => (
  !n ? null : (
    <span style={{
      fontSize: 10, fontVariantNumeric: 'tabular-nums', color: 'var(--text-2)',
      background: 'var(--surface-2, var(--surface))', border: '1px solid var(--border)',
      borderRadius: 999, padding: '0 6px', marginLeft: 6,
    }}>{n.toLocaleString()}</span>
  )
);

const EmptyHint = ({ children }) => (
  <div style={{ fontSize: 11, color: 'var(--text-3)', textAlign: 'center', padding: '14px 8px' }}>
    {children || 'Empty'}
  </div>
);

const Kpi = ({ label, value, tone, onClick }) => (
  <div onClick={onClick}
    style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
             padding: '8px 12px', minWidth: 150, flex: '1 1 150px',
             cursor: onClick ? 'pointer' : undefined }}
    title={onClick ? 'Open' : undefined}>
    <div style={{ fontSize: 20, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                  color: tone || 'var(--text-1)' }}>{value ?? '—'}</div>
    <div style={{ fontSize: 10, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {label}{onClick ? ' →' : ''}
    </div>
  </div>
);

const QueueCard = ({ title, count, children, accent, help }) => (
  <div style={{ flex: '1 1 260px', minWidth: 240, border: '1px solid var(--border)',
                borderLeft: `3px solid ${accent || 'var(--border)'}`, borderRadius: 8,
                background: 'var(--surface)', display: 'flex', flexDirection: 'column', maxHeight: 320 }}>
    <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-1)', display: 'flex', alignItems: 'center' }}>
        {title}{help && <window.HelpTip text={help} size={12} />}<CountBadge n={count} />
      </div>
    </div>
    <div style={{ overflowY: 'auto', padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
      {children}
    </div>
  </div>
);

// Where a row's status came from — the cleaning view's honesty line. The
// 08-19 backfill derived 200K statuses from DealCloud tags; those deserve
// the least trust and the loudest label.
const sourceLine = (row) => {
  const s = row.source;
  if (!s) return 'No status history recorded';
  if (s.actor === 'backfill') return 'Derived from DealCloud tags (backfill) — no live event since';
  if (s.manual) return `Set manually by ${s.actor}${s.days_ago != null ? ` · ${s.days_ago}d ago` : ''}`;
  return `${s.provider || 'provider'} event${s.days_ago != null ? ` · ${s.days_ago}d ago` : ''}`;
};

// One triage row in the cleaning view: identity + provenance + the raw DC
// tags + the latest note, and the three verdicts a human can reach —
// still real (re-confirm), overstated (downgrade), or dead (close out).
const CleaningRow = ({ row, onOpen, onOpenCompany, onDone }) => {
  const [busy, setBusy] = React.useState(false);
  const act = async (body) => {
    if (busy) return;
    setBusy(true);
    try { await window.AtlasApi.setMarketingStatus(row.contact_id, body); onDone(); }
    catch (e) { alert(`Could not update: ${e?.message || e}`); }
    finally { setBusy(false); }
  };
  const btn = (label, onClick, title) => (
    <button key={label} onClick={onClick} disabled={busy} title={title}
      style={{ background: 'var(--surface-2, var(--surface))', border: '1px solid var(--border)',
               borderRadius: 4, color: 'var(--text-2)', fontSize: 10, cursor: 'pointer',
               padding: '1px 6px', whiteSpace: 'nowrap' }}>{label}</button>
  );
  const fromBackfill = row.source?.actor === 'backfill';
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)',
                  padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={onOpen}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                   color: 'var(--text-1)', fontSize: 12, fontWeight: 600 }}
          title={row.email || ''}>{row.name || row.email || 'Unknown'}</button>
        {row.company && (
          <button onClick={onOpenCompany}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                     color: 'var(--text-2)', fontSize: 11, textDecoration: 'underline',
                     textDecorationColor: 'var(--border)', textUnderlineOffset: 2 }}>
            {row.company}</button>
        )}
        <span style={{ fontSize: 10, color: 'var(--text-3)' }}>
          {row.industry ? `${row.industry} · ` : ''}{row.owner || 'unassigned'}
        </span>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 10, border: '1px solid var(--border)', borderRadius: 4,
                       padding: '0 5px', color: 'var(--text-2)' }}>
          {MKT_LABELS[row.status] || row.status}
        </span>
        <AgeChip days={row.days_in_status} escalation={365} />
      </div>
      <div style={{ fontSize: 10, color: fromBackfill ? 'var(--amber-fg, #93540a)' : 'var(--text-3)' }}>
        {sourceLine(row)}
      </div>
      {!!(row.tags || []).length && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {row.tags.map((t) => (
            <span key={t} style={{ fontSize: 9, border: '1px solid var(--border)', borderRadius: 999,
                                   padding: '0 6px', color: 'var(--text-3)' }}>{t}</span>
          ))}
        </div>
      )}
      {row.last_note && (
        <div style={{ fontSize: 10, color: 'var(--text-3)', overflow: 'hidden',
                      textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          📝 {row.last_note.days_ago != null ? `${row.last_note.days_ago}d ago · ` : ''}
          {row.last_note.title ? `${row.last_note.title}: ` : ''}{row.last_note.snippet}
        </div>
      )}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        {btn('✓ Still real', () => act({ status: row.status }),
             'Re-confirm: keeps the status and resets its age')}
        {row.status === 'call_scheduled' &&
          btn('↓ Downgrade to Responded', () => act({ status: 'responded' }),
              'No call actually on the calendar — back to Responded')}
        <span style={{ flex: 1 }} />
        <CloseOut card={row} onDone={onDone} />
      </div>
    </div>
  );
};

// ── Report tab ────────────────────────────────────────────────────────────
// GET /api/marketing/email-report (app/email_report.py). Step = position in
// the SuperSend sequence; step 1 is the initial email ("stage 1"). Rows that
// aren't a credited dealmaker (unassigned mailbox, broadcasts, sender not
// recorded yet) render muted and stay out of the dealmaker totals.
const STEP_LABELS = {
  step_1: 'Step 1 · initial', step_2: 'Step 2', step_3: 'Step 3', step_4: 'Step 4',
  step_5: 'Step 5', step_6: 'Step 6', step_7_plus: 'Step 7+', no_step: 'No step', total: 'Total',
};

const fmtWeek = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

const RptTh = ({ children, left }) => (
  <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap',
               textAlign: left ? 'left' : 'right', background: 'var(--surface-2, var(--surface))',
               fontSize: 10, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
    {children}
  </th>
);

const RptTd = ({ children, left, muted, strong }) => (
  <td style={{ padding: '5px 8px', whiteSpace: 'nowrap', textAlign: left ? 'left' : 'right',
               fontVariantNumeric: 'tabular-nums', fontWeight: strong ? 700 : 400,
               color: muted ? 'var(--text-3)' : 'var(--text-1)' }}>
    {children}
  </td>
);

const EmailReport = ({ chip }) => {
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [week, setWeek] = React.useState('');            // '' = newest week
  const [trendMetric, setTrendMetric] = React.useState('step_1');
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    let live = true;
    window.AtlasApi.marketingEmailReport({ weeks: 8 })
      .then((r) => { if (live) setData(r); })
      .catch((e) => { if (live) setErr(String(e?.message || e)); });
    return () => { live = false; };
  }, []);

  if (err) {
    return <div style={{ color: 'var(--rose-fg, #b42318)', fontSize: 12 }}>Couldn’t load the email report: {err}</div>;
  }
  if (!data) return <EmptyHint>Loading…</EmptyHint>;

  const weeks = data.weeks || [];                         // newest first
  const sel = week || weeks[0];
  const rows = data.rows.filter((r) => r.week === sel);
  // Columns stay put while flipping weeks: a step shows if any week used it.
  const cols = data.steps.filter((k) => k === 'total' || data.rows.some((r) => r[k] > 0));
  const credited = rows.filter((r) => r.kind === 'dealmaker');
  const sum = (list, k) => list.reduce((a, r) => a + (r[k] || 0), 0);
  const unrecorded = sum(rows.filter((r) => r.kind === 'unrecorded'), 'total');
  const isThisWeek = sel === weeks[0];

  const copy = () => {
    const head = ['Dealmaker', ...cols.map((k) => STEP_LABELS[k] || k)];
    const lines = [head, ...rows.map((r) => [r.dealmaker, ...cols.map((k) => r[k])]),
                   ['Dealmakers total', ...cols.map((k) => sum(credited, k))]];
    const tsv = lines.map((l) => l.join('\t')).join('\n');
    navigator.clipboard?.writeText(tsv).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  };

  // Trend: one row per sender label, weeks oldest → newest.
  const trendWeeks = [...weeks].reverse();
  const labels = [...new Set(data.rows.filter((r) => r.kind !== 'broadcast').map((r) => r.dealmaker))]
    .sort((a, b) => {
      const ka = data.rows.find((r) => r.dealmaker === a)?.kind === 'dealmaker' ? 0 : 1;
      const kb = data.rows.find((r) => r.dealmaker === b)?.kind === 'dealmaker' ? 0 : 1;
      return ka - kb || a.localeCompare(b);
    });
  const cell = (label, w) => data.rows.find((r) => r.dealmaker === label && r.week === w)?.[trendMetric] || 0;
  const kindOf = (label) => data.rows.find((r) => r.dealmaker === label)?.kind;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)' }}>Emails sent by dealmaker</div>
          <window.Picker value={sel} onChange={setWeek} size="sm" placeholder="Week" menuMinWidth={220}
            options={weeks.map((w, i) => ({ value: w, label: `Week of ${fmtWeek(w)}`, hint: i === 0 ? 'this week' : i === 1 ? 'last week' : undefined }))} />
          <span style={{ flex: 1 }} />
          <button onClick={copy} title="Copy this week's table (paste into a sheet or email)"
            style={{ fontSize: 11, background: 'none', border: '1px solid var(--border)', borderRadius: 6,
                     color: 'var(--text-2)', cursor: 'pointer', padding: '3px 10px' }}>
            {copied ? 'Copied' : 'Copy table'}
          </button>
        </div>
        <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 11 }}>
            <thead>
              <tr>
                <RptTh left>Dealmaker</RptTh>
                {cols.map((k) => <RptTh key={k}>{STEP_LABELS[k] || k}</RptTh>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.dealmaker} style={{ borderBottom: '1px solid var(--border)' }}>
                  <RptTd left muted={r.kind !== 'dealmaker'}>{r.dealmaker}</RptTd>
                  {cols.map((k) => (
                    <RptTd key={k} muted={r.kind !== 'dealmaker' || !r[k]} strong={k === 'total'}>
                      {r[k] ? r[k].toLocaleString() : '—'}
                    </RptTd>
                  ))}
                </tr>
              ))}
              {!!credited.length && (
                <tr style={{ background: 'var(--surface-2, var(--surface))' }}>
                  <RptTd left strong>Dealmakers total</RptTd>
                  {cols.map((k) => <RptTd key={k} strong>{sum(credited, k).toLocaleString()}</RptTd>)}
                </tr>
              )}
            </tbody>
          </table>
          {!rows.length && <EmptyHint>No SuperSend sends recorded this week{isThisWeek ? ' yet' : ''}.</EmptyHint>}
        </div>
        {unrecorded > 0 && (
          <div style={{ fontSize: 11, color: 'var(--text-3)', lineHeight: 1.5 }}>
            <span style={{ color: 'var(--amber-fg, #93540a)' }}>
              {unrecorded.toLocaleString()} of this week’s sends {unrecorded === 1 ? 'has' : 'have'} no
              sender or step recorded yet.
            </span>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)' }}>Last {weeks.length} weeks</div>
          <div style={{ display: 'flex', gap: 4 }}>
            {chip(trendMetric === 'step_1', 'Step 1 · initial', () => setTrendMetric('step_1'), 'tm-1')}
            {chip(trendMetric === 'total', 'All steps', () => setTrendMetric('total'), 'tm-all')}
          </div>
        </div>
        <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 11 }}>
            <thead>
              <tr>
                <RptTh left>Dealmaker</RptTh>
                {trendWeeks.map((w) => <RptTh key={w}>{fmtWeek(w)}</RptTh>)}
              </tr>
            </thead>
            <tbody>
              {labels.map((l) => (
                <tr key={l} style={{ borderBottom: '1px solid var(--border)' }}>
                  <RptTd left muted={kindOf(l) !== 'dealmaker'}>{l}</RptTd>
                  {trendWeeks.map((w) => {
                    const n = cell(l, w);
                    return <RptTd key={w} muted={kindOf(l) !== 'dealmaker' || !n}>{n ? n.toLocaleString() : '—'}</RptTd>;
                  })}
                </tr>
              ))}
              <tr style={{ background: 'var(--surface-2, var(--surface))' }}>
                <RptTd left strong>Dealmakers total</RptTd>
                {trendWeeks.map((w) => (
                  <RptTd key={w} strong>
                    {sum(data.rows.filter((r) => r.week === w && r.kind === 'dealmaker'), trendMetric).toLocaleString()}
                  </RptTd>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)' }}>Responses</div>
        <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 11 }}>
            <thead>
              <tr>
                <RptTh left>All seller campaigns</RptTh>
                {trendWeeks.map((w) => <RptTh key={w}>{fmtWeek(w)}</RptTh>)}
              </tr>
            </thead>
            <tbody>
              {[['replied', 'Replies'], ['interested', 'Interested / meeting request'],
                ['bounced', 'Bounces'], ['unsubscribed', 'Unsubscribes']].map(([k, label]) => (
                <tr key={k} style={{ borderBottom: '1px solid var(--border)' }}>
                  <RptTd left>{label}</RptTd>
                  {trendWeeks.map((w) => {
                    const n = data.activity?.[w]?.[k] || 0;
                    return <RptTd key={w} muted={!n}>{n ? n.toLocaleString() : '—'}</RptTd>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Deep link: #/marketing?scope=all|mine&channel=all|call (+ &view=board|table|cleaning|website;
// no view = the Report tab; scope defaults by role, channel to email). The router only maps the section; the query refinement is
// ours to read and write (same contract as the analytics dashboards' ?f= —
// writeHash won't clobber it).
const hashParams = () => {
  const m = (window.location.hash || '').match(/^#\/marketing\?(.+)$/);
  return new URLSearchParams(m ? m[1] : '');
};

// Admins read the firm, everyone else starts on their own book.
const defaultScopeFor = (role) => ((role || '').toLowerCase() === 'admin' ? 'all' : 'mine');

const MarketingView = ({ setCompanyId, setContactId, currentRole }) => {
  // An explicit ?scope= wins; otherwise the role default, applied once the
  // role arrives (whoami can resolve after mount) unless already flipped.
  const scopeTouched = React.useRef(!!hashParams().get('scope'));
  const [scope, setScopeRaw] = React.useState(() => {
    const s = hashParams().get('scope');
    return s === 'all' || s === 'mine' ? s : defaultScopeFor(currentRole);
  });
  const setScope = (s) => { scopeTouched.current = true; setScopeRaw(s); };
  React.useEffect(() => {
    if (!scopeTouched.current && currentRole) setScopeRaw(defaultScopeFor(currentRole));
  }, [currentRole]);
  // Email by default — it's the Emails page; cold-call dispositions (the
  // dialer's contacts, many never emailed) are one chip away, and their
  // full history lives on the contact page.
  const [channel, setChannel] = React.useState(() => {
    const c = hashParams().get('channel'); // 'all' → null | 'email' | 'call'
    return c === 'all' ? null : c === 'call' ? 'call' : 'email';
  });
  const [limit, setLimit] = React.useState(80);
  const [board, setBoard] = React.useState(null);
  const [queues, setQueues] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const seqRef = React.useRef(0);

  // report (default) | board | table | cleaning | website — deep-linked as &view=.
  const [mode, setMode] = React.useState(() => {
    const v = hashParams().get('view');
    return v === 'board' || v === 'cleaning' || v === 'table' || v === 'website' ? v : 'report';
  });
  // Board/table/cleaning share the working lens; Report and Website don't.
  const lens = mode !== 'report' && mode !== 'website';
  const [cStatus, setCStatus] = React.useState(null);   // null=both | responded | call_scheduled
  const [cStale, setCStale] = React.useState(true);     // true = 12mo+ only, false = all ages
  const [cLimit, setCLimit] = React.useState(50);
  const [cleaning, setCleaning] = React.useState(null);
  const cSeqRef = React.useRef(0);

  // Working-lens filters (board + table): industry, age-in-status, PSAC.
  const [industry, setIndustry] = React.useState(() => hashParams().get('industry') || '');
  const [agePreset, setAgePreset] = React.useState(() => {
    const a = hashParams().get('age');
    return a === 'fresh' || a === 'mid' || a === 'old' ? a : '';
  });
  const [psacOnly, setPsacOnly] = React.useState(() => hashParams().get('psac') === '1');
  // Per-dealmaker lens — "los leads divididos por a qué DM están asignados".
  // Essential for associates on more than one team (their Mine spans
  // several books) and for anyone slicing Everyone by dealmaker.
  const [dm, setDm] = React.useState(() => hashParams().get('dm') || '');
  const [dealmakers, setDealmakers] = React.useState([]);
  const [industries, setIndustries] = React.useState([]);
  React.useEffect(() => {
    window.AtlasApi.listTeamMembers().then((r) => {
      const rows = Array.isArray(r) ? r : r?.members || [];
      setDealmakers(rows
        .filter((m) => (m.role || '').toLowerCase() === 'dealmaker' && m.active !== false)
        .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '')));
    }).catch(() => {});
  }, []);
  React.useEffect(() => {
    window.AtlasApi.listIndustries().then((r) =>
      setIndustries((Array.isArray(r) ? r : r?.industries || [])
        .filter((i) => i.active !== false))).catch(() => {});
  }, []);

  // Column totals load separately from the cards: the aggregation is the
  // page's one slow query (seconds when Postgres is cold), so cards paint
  // first and the badges hydrate when /board/counts answers.
  const [countsData, setCountsData] = React.useState(null);

  // Table tab (the raw-DealCloud-tags view).
  const [tData, setTData] = React.useState(null);
  const [tSort, setTSort] = React.useState('newest');
  const [tStatus, setTStatus] = React.useState('');
  const [tFrom, setTFrom] = React.useState('');
  const [tTo, setTTo] = React.useState('');
  const [tLimit, setTLimit] = React.useState(100);
  const tSeqRef = React.useRef(0);

  const AGE_PRESET_PARAMS = {
    fresh: { max_days: 90 },
    mid:   { min_days: 90, max_days: 365 },
    old:   { min_days: 365 },
  };
  const filterParams = React.useMemo(() => ({
    ...(industry ? { industry } : {}),
    ...(psacOnly ? { psac: true } : {}),
    ...(dm ? { dealmaker: dm } : {}),
    ...(AGE_PRESET_PARAMS[agePreset] || {}),
  }), [industry, psacOnly, agePreset, dm]);

  const refresh = React.useCallback(() => {
    setErr(null);
    const p = { scope, limit, ...(channel ? { channel } : {}), ...filterParams };
    const seq = ++seqRef.current; // rapid tab flips: last request wins,
    setLoading(true);             // stragglers from older ones are dropped
    window.AtlasApi.marketingBoard(p)
      .then((b) => { if (seqRef.current === seq) { setBoard(b); setLoading(false); } })
      .catch((e) => { if (seqRef.current === seq) { setErr(String(e?.message || e)); setLoading(false); } });
    window.AtlasApi.marketingBoardCounts(p)
      .then((c) => { if (seqRef.current === seq) setCountsData(c); })
      .catch(() => { if (seqRef.current === seq) setCountsData(null); });
    window.AtlasApi.marketingQueues({ scope, ...(channel ? { channel } : {}) })
      .then((q) => { if (seqRef.current === seq) setQueues(q); })
      .catch(() => { if (seqRef.current === seq) setQueues(null); });
  }, [scope, channel, limit, filterParams]);

  // Stale-while-revalidate: keep showing the previous board while the next
  // one loads — flipping tabs must never blank the page back to a skeleton.
  // Board tab only: its owner-scoped reads are the page's slow ones, and
  // the Report tab (the default) needs none of them.
  React.useEffect(() => { if (mode === 'board') refresh(); }, [mode, refresh]);

  const refreshTable = React.useCallback(() => {
    const seq = ++tSeqRef.current;
    const p = { scope, sort: tSort, limit: tLimit,
                ...(channel ? { channel } : {}),
                ...(tStatus ? { status: tStatus } : {}),
                ...(industry ? { industry } : {}),
                ...(psacOnly ? { psac: true } : {}),
                ...(dm ? { dealmaker: dm } : {}),
                ...(tFrom ? { since: tFrom } : {}),
                ...(tTo ? { until: tTo } : {}) };
    window.AtlasApi.marketingTable(p)
      .then((r) => { if (tSeqRef.current === seq) setTData(r); })
      .catch((e) => { if (tSeqRef.current === seq) setErr(String(e?.message || e)); });
  }, [scope, channel, tSort, tStatus, tLimit, industry, psacOnly, dm, tFrom, tTo]);

  React.useEffect(() => { if (mode === 'table') refreshTable(); }, [mode, refreshTable]);

  const refreshCleaning = React.useCallback(() => {
    const seq = ++cSeqRef.current;
    const p = { scope, limit: cLimit,
                ...(channel ? { channel } : {}),
                ...(cStatus ? { status: cStatus } : {}),
                ...(dm ? { dealmaker: dm } : {}),
                ...(cStale ? {} : { min_age_days: 0 }) };
    window.AtlasApi.marketingCleaning(p)
      .then((r) => { if (cSeqRef.current === seq) setCleaning(r); })
      .catch((e) => { if (cSeqRef.current === seq) setErr(String(e?.message || e)); });
  }, [scope, channel, cStatus, cStale, cLimit, dm]);

  React.useEffect(() => { if (mode === 'cleaning') refreshCleaning(); }, [mode, refreshCleaning]);

  // Mirror the tab state into the URL so the address bar is always a
  // shareable link to exactly this view. replaceState, not pushState —
  // flipping tabs shouldn't pile up back-button entries.
  React.useEffect(() => {
    const cur = window.location.hash || '';
    // Only refine our own section, and only once it's in the URL. This effect
    // runs before app.jsx's writeHash on mount (child effects first) — writing
    // here then would replace the previous page's history entry and starve
    // writeHash's pushState, breaking the back button.
    if (!cur.startsWith('#/marketing')) return;
    const qs = new URLSearchParams();
    if (scope !== defaultScopeFor(currentRole)) qs.set('scope', scope);
    if (channel !== 'email') qs.set('channel', channel || 'all');
    if (mode !== 'report') qs.set('view', mode);
    if (industry) qs.set('industry', industry);
    if (agePreset) qs.set('age', agePreset);
    if (psacOnly) qs.set('psac', '1');
    if (dm) qs.set('dm', dm);
    const next = '#/marketing' + (qs.toString() ? '?' + qs.toString() : '');
    if (cur === next) return;
    try { history.replaceState(null, '', next); } catch (_) { window.location.hash = next.slice(1); }
  }, [scope, channel, mode, industry, agePreset, psacOnly, dm, currentRole]);

  const openContact = (card) => { if (card.contact_id && setContactId) setContactId(card.contact_id); };
  const openCompany = (card) => { if (card.company_id && setCompanyId) setCompanyId(card.company_id); };

  const columns = board?.columns || [];
  const byStatus = Object.fromEntries(columns.map((c) => [c.status, c]));
  const nextOf = (status) => MKT_FLOW[MKT_FLOW.indexOf(status) + 1] || null;
  // Totals hydrate from /board/counts after the cards paint.
  const totals = countsData?.counts || null;
  const totalOf = (status) => (totals ? (totals[status] || 0) : null);
  const flowTotal = totals ? MKT_FLOW.reduce((a, s) => a + (totals[s] || 0), 0) : null;

  // Waiting/Moving ARE the board's responded / call_scheduled columns —
  // one fetch feeds both the queues and the board.
  const escalation = board?.escalation_days || 30;
  const waitingCol = byStatus.responded;
  const movingCol = byStatus.call_scheduled;
  const oldestWaiting = waitingCol?.cards?.length ? waitingCol.cards[0].days_in_status : null;
  const cleaningCount = (queues?.cleaning?.unmatched_events?.length || 0)
    + (queues?.cleaning?.stale_interest_count || 0);

  const chip = (active, label, onClick, key) => (
    <button key={key || label} onClick={onClick}
      style={{
        fontSize: 11, padding: '3px 10px', borderRadius: 6, cursor: 'pointer',
        border: '1px solid var(--border)',
        background: active ? 'var(--accent-bg, var(--surface-2, var(--surface)))' : 'transparent',
        color: active ? 'var(--text-1)' : 'var(--text-3)',
        fontWeight: active ? 700 : 400,
      }}>{label}</button>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        breadcrumb={['Workspace', 'Emails']}
        title="Emails"
        meta={!lens ? ''
          : !board ? (mode === 'board' ? 'Loading…' : '')
          : flowTotal == null ? `Counting…${loading ? ' · refreshing…' : ''}`
          : flowTotal
            ? `${flowTotal.toLocaleString()} contacts in the flow${loading ? ' · refreshing…' : ''}`
            : 'No contacts match — clear a filter'}
        actions={(
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: 4 }}>
              {chip(mode === 'report', 'Report', () => setMode('report'), 'md-report')}
              {chip(mode === 'board', 'Board', () => setMode('board'), 'md-board')}
              {chip(mode === 'table', 'Table', () => setMode('table'), 'md-table')}
              {chip(mode === 'website', 'Website', () => setMode('website'), 'md-website')}
            </div>
            {lens && (<>
              <div style={{ width: 1, height: 16, background: 'var(--border)' }} />
              <div style={{ display: 'flex', gap: 4 }}>
                {chip(channel === null, 'All', () => setChannel(null), 'ch-all')}
                {chip(channel === 'email', 'Email', () => setChannel('email'), 'ch-email')}
                {chip(channel === 'call', 'Cold calls', () => setChannel('call'), 'ch-call')}
              </div>
              <div style={{ width: 1, height: 16, background: 'var(--border)' }} />
              <div style={{ display: 'flex', gap: 4 }}>
                {chip(scope === 'mine', 'Mine', () => setScope('mine'), 'sc-mine')}
                {chip(scope === 'all', 'Everyone', () => setScope('all'), 'sc-all')}
              </div>
            </>)}
          </div>
        )}
      />

      <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 16,
                    opacity: loading && board && mode === 'board' ? 0.55 : 1, transition: 'opacity 120ms ease' }}>
        {err && lens && (
          <div style={{ color: 'var(--rose-fg, #b42318)', fontSize: 12 }}>
            Couldn’t load the board: {err}
          </div>
        )}

        {mode === 'report' && <EmailReport chip={chip} />}
        {mode === 'website' && <window.WebsiteVisitorsPanel chip={chip} setContactId={setContactId} setCompanyId={setCompanyId}
          canWrite={currentRole !== 'viewer'} />}

        {/* Filter bar — the working lens (board + table) */}
        {lens && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <window.Picker value={dm} onChange={setDm} size="sm" emptyLabel="All dealmakers" placeholder="Dealmaker" style={{ maxWidth: 180 }}
            options={dealmakers.map((m) => ({ value: String(m.id), label: m.full_name }))} />
          <window.Picker value={industry} onChange={setIndustry} size="sm" emptyLabel="All industries" placeholder="Industry" style={{ maxWidth: 200 }}
            options={industries.map((i) => ({ value: String(i.id), label: i.name }))} />
          <div style={{ display: 'flex', gap: 4 }}>
            {chip(agePreset === '', 'Any age', () => setAgePreset(''), 'ag-any')}
            {chip(agePreset === 'fresh', '≤ 90d', () => setAgePreset('fresh'), 'ag-fresh')}
            {chip(agePreset === 'mid', '90d – 1y', () => setAgePreset('mid'), 'ag-mid')}
            {chip(agePreset === 'old', '> 1y', () => setAgePreset('old'), 'ag-old')}
          </div>
          {chip(psacOnly, 'In PSAC', () => setPsacOnly(!psacOnly), 'psac-only')}
          {(industry || agePreset || psacOnly || dm) && (
            <button onClick={() => { setIndustry(''); setAgePreset(''); setPsacOnly(false); setDm(''); }}
              style={{ fontSize: 11, background: 'none', border: 'none', cursor: 'pointer',
                       color: 'var(--text-3)', textDecoration: 'underline',
                       textDecorationColor: 'var(--border)', textUnderlineOffset: 2 }}>
              Clear filters
            </button>
          )}
          {mode === 'table' && (
            <>
              <div style={{ width: 1, height: 16, background: 'var(--border)' }} />
              <window.Picker value={tStatus} onChange={setTStatus} size="sm" emptyLabel="Any status" placeholder="Status"
                options={[...MKT_FLOW, ...Object.keys(MKT_EXIT_LABELS)].map((s) => ({ value: s, label: MKT_LABELS[s] || MKT_EXIT_LABELS[s] || s }))} />
              <label style={{ fontSize: 10, color: 'var(--text-3)', display: 'flex', gap: 4, alignItems: 'center' }}>
                from <input type="date" value={tFrom} onChange={(e) => setTFrom(e.target.value)}
                  style={{ fontSize: 11, padding: '2px 4px', borderRadius: 6,
                           background: 'var(--surface)', color: 'var(--text-1)', border: '1px solid var(--border)' }} />
              </label>
              <label style={{ fontSize: 10, color: 'var(--text-3)', display: 'flex', gap: 4, alignItems: 'center' }}>
                to <input type="date" value={tTo} onChange={(e) => setTTo(e.target.value)}
                  style={{ fontSize: 11, padding: '2px 4px', borderRadius: 6,
                           background: 'var(--surface)', color: 'var(--text-1)', border: '1px solid var(--border)' }} />
              </label>
              <div style={{ display: 'flex', gap: 4 }}>
                {chip(tSort === 'newest', 'Newest', () => setTSort('newest'), 'ts-new')}
                {chip(tSort === 'oldest', 'Oldest', () => setTSort('oldest'), 'ts-old')}
              </div>
            </>
          )}
        </div>
        )}

        {/* KPI strip */}
        {mode === 'board' && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Kpi label="Responded, awaiting action" value={totalOf('responded')}
               tone={totalOf('responded') ? 'var(--rose-fg, #b42318)' : undefined} />
          <Kpi label="Oldest waiting (days)" value={oldestWaiting}
               tone={ageColor(oldestWaiting, escalation)} />
          <Kpi label="Calls scheduled" value={totalOf('call_scheduled')} />
          <Kpi label="Needs cleaning" value={cleaningCount}
               onClick={() => setMode('cleaning')} />
          <Kpi label="Disposition completeness"
               value={queues?.disposition_completeness != null ? `${queues.disposition_completeness}%` : null}
               tone={queues?.disposition_completeness != null && queues.disposition_completeness < 70
                     ? 'var(--rose-fg, #b42318)' : undefined} />
        </div>
        )}

        {mode === 'table' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
              {tData ? `${tData.total.toLocaleString()} contacts` : 'Loading…'}
            </div>
            <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 11 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: 'var(--text-3)', fontSize: 10,
                               textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {['Contact', 'Company', 'Industry', 'Owner', 'Status', 'Age', 'Channel', 'PSAC', 'DealCloud tags'].map((h) => (
                      <th key={h} style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)',
                                           whiteSpace: 'nowrap', background: 'var(--surface-2, var(--surface))' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(tData?.rows || []).map((r) => (
                    <tr key={r.contact_id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '5px 8px', whiteSpace: 'nowrap' }}>
                        <button onClick={() => openContact(r)} title={r.email || ''}
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                                   color: 'var(--text-1)', fontSize: 11, fontWeight: 600 }}>
                          {r.name || r.email || 'Unknown'}
                        </button>
                      </td>
                      <td style={{ padding: '5px 8px', whiteSpace: 'nowrap', maxWidth: 180,
                                   overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {r.company_id ? (
                          <button onClick={() => openCompany(r)}
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                                     color: 'var(--text-2)', fontSize: 11, textDecoration: 'underline',
                                     textDecorationColor: 'var(--border)', textUnderlineOffset: 2 }}>
                            {r.company}
                          </button>
                        ) : (r.company || '—')}
                      </td>
                      <td style={{ padding: '5px 8px', whiteSpace: 'nowrap', color: 'var(--text-2)' }}>{r.industry || '—'}</td>
                      <td style={{ padding: '5px 8px', whiteSpace: 'nowrap', color: 'var(--text-2)' }}>{r.owner || '—'}</td>
                      <td style={{ padding: '5px 8px', whiteSpace: 'nowrap' }}>
                        <span style={{ fontSize: 10, border: '1px solid var(--border)', borderRadius: 4,
                                       padding: '0 5px', color: 'var(--text-2)' }}>
                          {MKT_LABELS[r.status] || MKT_EXIT_LABELS[r.status] || r.status || 'no status'}
                        </span>
                      </td>
                      <td style={{ padding: '5px 8px' }}><AgeChip days={r.days_in_status} escalation={365} /></td>
                      <td style={{ padding: '5px 8px', color: 'var(--text-3)' }}>{r.channel || '—'}</td>
                      <td style={{ padding: '5px 8px' }}><PsacBadge psac={r.psac} /></td>
                      <td style={{ padding: '5px 8px', minWidth: 260 }}>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {(r.tags || []).map((t) => (
                            <span key={t} style={{ fontSize: 9, border: '1px solid var(--border)',
                                                   borderRadius: 999, padding: '0 6px', color: 'var(--text-3)' }}>{t}</span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {tData && !tData.rows.length && <EmptyHint>No contacts match these filters.</EmptyHint>}
              {!tData && <EmptyHint>Loading…</EmptyHint>}
            </div>
            {tData && tData.total > tData.rows.length && tLimit < 300 && (
              <button onClick={() => setTLimit(300)}
                style={{ fontSize: 11, color: 'var(--text-2)', background: 'none',
                         border: '1px dashed var(--border)', borderRadius: 6,
                         cursor: 'pointer', padding: '6px 0' }}>
                Show more · {(tData.total - tData.rows.length).toLocaleString()} more match
              </button>
            )}
          </div>
        )}

        {mode === 'cleaning' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <button onClick={() => setMode('board')}
                style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6,
                         color: 'var(--text-2)', fontSize: 11, cursor: 'pointer', padding: '3px 10px' }}>
                ← Board
              </button>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)' }}>
                Cleaning{cleaning ? ` · ${cleaning.total.toLocaleString()} to review` : ''}
              </div>
              <span style={{ flex: 1 }} />
              <div style={{ display: 'flex', gap: 4 }}>
                {chip(cStatus === null, 'Responded + Call scheduled', () => setCStatus(null), 'cs-both')}
                {chip(cStatus === 'responded', 'Responded', () => setCStatus('responded'), 'cs-resp')}
                {chip(cStatus === 'call_scheduled', 'Call scheduled', () => setCStatus('call_scheduled'), 'cs-call')}
              </div>
              <div style={{ width: 1, height: 16, background: 'var(--border)' }} />
              <div style={{ display: 'flex', gap: 4 }}>
                {chip(cStale, 'Stale 12mo+', () => setCStale(true), 'ca-stale')}
                {chip(!cStale, 'All ages', () => setCStale(false), 'ca-all')}
              </div>
            </div>
            {!cleaning && <EmptyHint>Loading…</EmptyHint>}
            {cleaning && !cleaning.rows.length && <EmptyHint>Nothing to clean here. 🎉</EmptyHint>}
            {(cleaning?.rows || []).map((r) => (
              <CleaningRow key={r.contact_id} row={r}
                onOpen={() => openContact(r)} onOpenCompany={() => openCompany(r)}
                onDone={() => { refreshCleaning(); refresh(); }} />
            ))}
            {cleaning && cleaning.total > cleaning.rows.length && cLimit < 200 && (
              <button onClick={() => setCLimit(200)}
                style={{ fontSize: 11, color: 'var(--text-2)', background: 'none',
                         border: '1px dashed var(--border)', borderRadius: 6,
                         cursor: 'pointer', padding: '6px 0' }}>
                Show more · {(cleaning.total - cleaning.rows.length).toLocaleString()} remaining
              </button>
            )}
            {cleaning && cleaning.total > cleaning.rows.length && cLimit >= 200 && (
              <div style={{ fontSize: 10, color: 'var(--text-3)', textAlign: 'center' }}>
                +{(cleaning.total - cleaning.rows.length).toLocaleString()} more — they’ll surface here
                as you work the list down, oldest first
              </div>
            )}
          </div>
        )}

        {mode === 'board' && (<>
        {/* The three queues */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <QueueCard title="Waiting for classification" count={totalOf('responded')}
            help="Responded, no outcome yet — oldest first. Empties when every response has a next step or an exit."
            accent="var(--rose-fg, #b42318)">
            {!board && <EmptyHint>Loading…</EmptyHint>}
            {board && !waitingCol?.cards?.length && <EmptyHint>Every response has a next step.</EmptyHint>}
            {(waitingCol?.cards || []).map((c) => (
              <MktCard key={c.contact_id} card={c} escalation={escalation}
                onOpen={() => openContact(c)} onOpenCompany={() => openCompany(c)}
                actions={(
                  <>
                    <Advance card={c} next="call_scheduled" nextLabel="Call scheduled" onDone={refresh} />
                    <CloseOut card={c} onDone={refresh} />
                  </>
                )} />
            ))}
          </QueueCard>

          <QueueCard title="Moving" count={totalOf('call_scheduled')}
            help="Calls on the calendar — hold them or record the no-show."
            accent="var(--amber-fg, #93540a)">
            {!board && <EmptyHint>Loading…</EmptyHint>}
            {board && !movingCol?.cards?.length && <EmptyHint>No calls on the calendar.</EmptyHint>}
            {(movingCol?.cards || []).map((c) => (
              <MktCard key={c.contact_id} card={c} onOpen={() => openContact(c)}
                onOpenCompany={() => openCompany(c)}
                actions={(
                  <>
                    <Advance card={c} next="ds_call_held" nextLabel="Held" onDone={refresh} />
                    <CloseOut card={c} onDone={refresh} />
                  </>
                )} />
            ))}
          </QueueCard>

          <QueueCard title="Needs cleaning" count={cleaningCount}
            help="Unmatched provider events, plus interest older than 12 months.">
            {!queues && <EmptyHint>Loading…</EmptyHint>}
            {queues && !queues.cleaning?.unmatched_events?.length
              && !(queues.cleaning?.stale_interest_count > 0) && <EmptyHint>Clean.</EmptyHint>}
            {(queues?.cleaning?.unmatched_events || []).map((u) => (
              <div key={u.email} style={{ fontSize: 11, color: 'var(--text-2)', display: 'flex', gap: 6 }}>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.email}</span>
                <span style={{ color: 'var(--text-3)' }}>{u.events} events, no contact</span>
              </div>
            ))}
            {(queues?.cleaning?.stale_interest_count || 0) > 0 && (
              <button onClick={() => setMode('cleaning')}
                style={{ fontSize: 11, color: 'var(--text-2)', background: 'none', textAlign: 'left',
                         border: '1px dashed var(--border)', borderRadius: 6, cursor: 'pointer',
                         padding: '4px 6px' }}>
                {queues.cleaning.stale_interest_count.toLocaleString()} contacts with interest &gt; 12 months old — review them →
              </button>
            )}
          </QueueCard>
        </div>

        {/* PSAC board */}
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase',
                        letterSpacing: '0.05em', marginBottom: 6 }}>
            PSAC board{countsData?.exits && Object.values(countsData.exits).some(Boolean)
              ? ` · closed out: ${Object.entries(countsData.exits).filter(([, n]) => n).map(([k, n]) => `${MKT_EXIT_LABELS[k] || k} ${n.toLocaleString()}`).join(' · ')}`
              : ''}
          </div>
          <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 8 }}>
            {MKT_FLOW.map((status) => {
              const col = byStatus[status];
              const hot = ['responded', 'call_scheduled', 'ds_call_held'].includes(status);
              return (
                <div key={status} style={{
                  flex: '0 0 220px', border: '1px solid var(--border)', borderRadius: 8,
                  background: hot ? 'var(--surface)' : 'var(--surface-2, var(--surface))',
                  display: 'flex', flexDirection: 'column', maxHeight: 460,
                }}>
                  <div style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', display: 'flex',
                                alignItems: 'center', fontSize: 11, fontWeight: 700, color: 'var(--text-1)' }}>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {MKT_LABELS[status] || col?.label || status}
                    </span>
                    <CountBadge n={totalOf(status)} />
                  </div>
                  <div style={{ overflowY: 'auto', padding: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {!board && <EmptyHint>Loading…</EmptyHint>}
                    {board && col && !col.cards?.length && <EmptyHint />}
                    {(col?.cards || []).map((c) => (
                      <MktCard key={c.contact_id} card={c} onOpen={() => openContact(c)}
                        onOpenCompany={() => openCompany(c)}
                        actions={(
                          <>
                            <Advance card={c} next={nextOf(status)}
                              nextLabel={MKT_LABELS[nextOf(status)] || ''} onDone={refresh} />
                            <CloseOut card={c} onDone={refresh} />
                          </>
                        )} />
                    ))}
                    {col && totalOf(status) > (col.cards || []).length && (
                      limit < 300 ? (
                        <button onClick={() => setLimit(300)}
                          style={{ fontSize: 10, color: 'var(--text-2)', background: 'none',
                                   border: '1px dashed var(--border)', borderRadius: 6,
                                   cursor: 'pointer', padding: '4px 0' }}>
                          Show more · {(totalOf(status) - col.cards.length).toLocaleString()} hidden
                        </button>
                      ) : (
                        <div style={{ fontSize: 10, color: 'var(--text-3)', textAlign: 'center' }}>
                          +{(totalOf(status) - col.cards.length).toLocaleString()} more — narrow with the filters above
                        </div>
                      )
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        </>)}
      </div>
    </div>
  );
};

window.MarketingView = MarketingView;
